'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
} from 'react';
import {
  DEFAULT_UNSAVED_MESSAGE,
  ROUTE_PROGRESS_CANCEL_EVENT,
  isGuardableAnchorClick,
  isLeavingNavigation,
  shouldConfirmLeave,
  type GuardDecisionInput,
} from './unsaved-changes-core';

/**
 * Unsaved-changes guard — DOM/React wiring.
 *
 * All decisions live in `unsaved-changes-core.ts` and are unit-tested. This file
 * is the thin shell that binds them to the document. It is deliberately dumb:
 * this repository has no jsdom, so nothing here is covered by an automated test
 * and it must be validated in a real browser before any form adopts it.
 *
 * ─── What this covers, and what it does not ───────────────────────────────
 *
 *   COVERED  in-app anchor / <Link> clicks — capture-phase listener on
 *            document, so it runs before Next's handler and can preventDefault.
 *   COVERED  browser back / forward — a sentinel history entry is pushed while
 *            dirty and re-pushed if the user cancels.
 *   COVERED  tab close / reload — beforeunload.
 *
 *   NOT COVERED  programmatic `router.push()` / `router.replace()`.
 *
 * The omission is deliberate and worth stating plainly, because the obvious
 * implementation is a trap. `router.push()` could be observed by patching
 * `history.pushState`, but by the time `pushState` runs Next has already
 * committed to the transition — the patch can watch it, not cancel it, so the
 * user would be asked to confirm a navigation that then happens regardless.
 * Worse, `route-progress-bar.tsx` already patches `history.pushState`, caching
 * the original at mount and restoring it on unmount; a second patch would be
 * silently discarded the first time either component unmounts.
 *
 * Programmatic navigation is therefore handled in a later PR by a wrapped
 * router that consumers call explicitly. Claiming coverage here that does not
 * exist is the same vacuous-assurance pattern this programme exists to remove.
 */

interface DirtySource {
  id: string;
  read: () => GuardDecisionInput;
  message: string;
}

interface GuardRegistry {
  register: (source: DirtySource) => () => void;
  /** Returns the message of the first blocking source, or null if none block. */
  blockingMessage: () => string | null;
}

const UnsavedChangesContext = createContext<GuardRegistry | null>(null);

/**
 * Mount once, high in the tree (the dashboard shell). Owns the single
 * document-level listener set so that N guarded forms do not each add their own.
 */
export function UnsavedChangesProvider({ children }: { children: React.ReactNode }) {
  const sourcesRef = useRef(new Map<string, DirtySource>());
  // Set while we are ourselves re-pushing a history entry after the user chose
  // to stay, so the resulting popstate is not treated as a fresh back-press.
  const suppressPopstateRef = useRef(false);

  const blockingMessage = useCallback((): string | null => {
    for (const source of sourcesRef.current.values()) {
      if (shouldConfirmLeave(source.read())) return source.message;
    }
    return null;
  }, []);

  const register = useCallback((source: DirtySource) => {
    sourcesRef.current.set(source.id, source);
    return () => {
      sourcesRef.current.delete(source.id);
    };
  }, []);

  const registry = useMemo<GuardRegistry>(
    () => ({ register, blockingMessage }),
    [register, blockingMessage],
  );

  useEffect(() => {
    function cancelRouteProgress() {
      // route-progress-bar starts its bar from its own capture-phase click
      // listener. Registration order between the two is not guaranteed, so it
      // may already have started when we cancel — tell it to reset, or the bar
      // hangs until its 12s safety ceiling.
      window.dispatchEvent(new Event(ROUTE_PROGRESS_CANCEL_EVENT));
    }

    // --- in-app link clicks -------------------------------------------------
    function onClickCapture(event: MouseEvent) {
      const element = event.target instanceof Element ? event.target.closest('a[href]') : null;
      const anchor = element instanceof HTMLAnchorElement ? element : null;
      if (
        !isGuardableAnchorClick(
          event,
          anchor && {
            href: anchor.href,
            target: anchor.target,
            hasDownload: anchor.hasAttribute('download'),
          },
        )
      ) {
        return;
      }
      if (!anchor || !isLeavingNavigation(anchor.href, window.location.href)) return;

      const message = blockingMessage();
      if (message === null) return;

      // A native confirm is intentional: it cannot be missed, needs no portal,
      // and works before any dialog component has hydrated. A styled dialog is a
      // later refinement, not a blocker.
      if (!window.confirm(message)) {
        event.preventDefault();
        event.stopPropagation();
        cancelRouteProgress();
      }
    }
    document.addEventListener('click', onClickCapture, true);

    // --- browser back / forward --------------------------------------------
    function onPopState() {
      if (suppressPopstateRef.current) {
        suppressPopstateRef.current = false;
        return;
      }
      const message = blockingMessage();
      if (message === null) return;
      // Native confirm, as above.
      if (!window.confirm(message)) {
        // The entry is already popped; put it back so the user stays put.
        suppressPopstateRef.current = true;
        history.forward();
        cancelRouteProgress();
      }
    }
    window.addEventListener('popstate', onPopState);

    // --- tab close / reload -------------------------------------------------
    function onBeforeUnload(event: BeforeUnloadEvent) {
      if (blockingMessage() === null) return;
      // Browsers ignore custom text and show their own copy; both assignments
      // are required for cross-browser support.
      event.preventDefault();
      event.returnValue = '';
    }
    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      document.removeEventListener('click', onClickCapture, true);
      window.removeEventListener('popstate', onPopState);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [blockingMessage]);

  return (
    <UnsavedChangesContext.Provider value={registry}>{children}</UnsavedChangesContext.Provider>
  );
}

export interface UseUnsavedChangesGuardOptions {
  /** True when the form holds edits that are not durably saved. */
  dirty: boolean;
  /** Message shown when leaving. Keep it specific about what is lost. */
  message?: string;
  /** A draft-restore prompt is awaiting the user's decision. */
  restorePending?: boolean;
}

export interface UnsavedChangesGuard {
  /**
   * Call synchronously at the top of the submit handler, BEFORE the first
   * await. Backed by a ref, not state, so React batching cannot leave it stale
   * during the `router.push` that follows a successful save.
   */
  beginSubmit: () => void;
  /** Call if the submit rejected and the user is staying on the page. */
  endSubmit: () => void;
  /** Call before a deliberate discard/delete that navigates away. */
  beginDiscard: () => void;
}

/**
 * Arms the guard for one form. No-ops safely when no provider is mounted, so a
 * form can adopt it before the provider ships.
 */
export function useUnsavedChangesGuard({
  dirty,
  message = DEFAULT_UNSAVED_MESSAGE,
  restorePending = false,
}: UseUnsavedChangesGuardOptions): UnsavedChangesGuard {
  const registry = useContext(UnsavedChangesContext);
  const id = useId();

  const submittingRef = useRef(false);
  const discardingRef = useRef(false);
  // Latest props read through a ref so the registered source never goes stale
  // without re-registering on every keystroke.
  const stateRef = useRef({ dirty, restorePending });
  stateRef.current = { dirty, restorePending };

  useEffect(() => {
    if (!registry) return;
    return registry.register({
      id,
      message,
      read: () => ({
        dirty: stateRef.current.dirty,
        submitting: submittingRef.current,
        restorePending: stateRef.current.restorePending,
        discarding: discardingRef.current,
      }),
    });
  }, [registry, id, message]);

  return useMemo(
    () => ({
      beginSubmit: () => {
        submittingRef.current = true;
      },
      endSubmit: () => {
        submittingRef.current = false;
      },
      beginDiscard: () => {
        discardingRef.current = true;
      },
    }),
    [],
  );
}
